'use memo'

import { REGISTRATION_TEAM_PARAM } from '@sportninja/common/features/registration/registrationUrlParams'
import { findDivisionTeamById } from '@sportninja/common/features/registration/startStep/divisionTeamIndex'
import type { DivisionTeam } from '@sportninja/common/features/registration/startStep/types'
import type { RegistrationTeam } from '@sportninja/common/hooks/useRegistration'
import type { Division } from '@sportninja/common/types'
import { usePreselectFromUrl } from '../../../hooks/usePreselectFromUrl'

interface Params {
  /**
   * The unfiltered top-level teams from the registration payload. Unfiltered
   * on purpose: the link's team has to be findable even when the current role
   * filter would hide its division, because resolving the team is what decides
   * the division.
   */
  teams: RegistrationTeam[] | null | undefined
  /** A pre-select only ever fills an empty choice. */
  selectedTeamId: string | null
  onSelect: (entry: DivisionTeam<Division>) => void
}

/**
 * Resolve `?team_id=` into a team AND the division that owns it.
 *
 * The existing team pre-select runs two steps later, against the user's own
 * teams from `useUserRegistrationTeams`. That list cannot exist yet here — it
 * is fetched from a division id, which is the very thing this is resolving —
 * so this matches against the registration payload's own team list instead.
 */
export const usePreselectedStartTeam = ({
  teams,
  selectedTeamId,
  onSelect,
}: Params): void => {
  usePreselectFromUrl<DivisionTeam<Division>>({
    param: REGISTRATION_TEAM_PARAM,
    // Deliberately NOT gated on the session's own team. Following a link that
    // names a team is a fresh, explicit choice, so it outranks whatever an
    // earlier pass left on the session — otherwise a reader who already
    // registered once could never be sent to a different team by a link, and
    // would see the old team silently instead of the one they clicked.
    //
    // It still latches after one run (see `usePreselectFromUrl`), so it fills
    // the choice once and then leaves the reader alone.
    enabled: Boolean(teams?.length) && !selectedTeamId,
    resolve: (teamId) => findDivisionTeamById(teams, teamId),
    onSelect,
  })
}
